import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaUserCircle, FaSyncAlt, FaHistory } from "react-icons/fa";
import { FiSettings } from "react-icons/fi";
import { HiEye } from "react-icons/hi";
import * as XLSX from 'xlsx';
import LeftPanelTopBar from "../LeftPanelTopBar";
import EditTicketModal from "./EditTicketModal";
import { TicketContext } from "../../../../app/providers/TicketContext";
import { PanelContext } from "../../../../app/providers/PanelContext";
import { TicketWebSocketContext } from "../../../websocket/TicketWebSocketProvider";
import FormView from "./FormView";
import "./LeftPanelTickets.css";

const WorkspaceOverlay = ({ data, ticketId, onClose }) => {
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [colFilters, setColFilters] = useState({});
  const [showFilterFor, setShowFilterFor] = useState(null);
  const filterRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (!showFilterFor) return;
    const h = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterFor(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showFilterFor]);

  const cellStr = (val) => {
    if (val === null || val === undefined || val === '') return '';
    if (Array.isArray(val) || (typeof val === 'object')) return JSON.stringify(val);
    return String(val);
  };

  const filtered = useMemo(() => {
    let rows = data.rows;
    const q = search.toLowerCase().trim();
    if (q) {
      rows = rows.filter((r) => data.columns.some((c) => cellStr(r[c]).toLowerCase().includes(q)));
    }
    Object.entries(colFilters).forEach(([col, val]) => {
      if (!val) return;
      const v = val.toLowerCase();
      rows = rows.filter((r) => cellStr(r[col]).toLowerCase().includes(v));
    });
    return rows;
  }, [data.rows, data.columns, search, colFilters]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const av = cellStr(a[sortCol]).toLowerCase();
      const bv = cellStr(b[sortCol]).toLowerCase();
      const na = Number(av), nb = Number(bv);
      if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const exportXLSX = () => {
    const headers = data.columns;
    const rows = sorted.map((r) => headers.map((c) => cellStr(r[c])));
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h, i) => ({
      wch: Math.min(Math.max(h.length, ...rows.map((r) => (r[i] || '').length)) + 2, 50),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Workspace');
    XLSX.writeFile(wb, `workspace_${data.flowId}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportCSV = () => {
    const headers = data.columns;
    const csv = [
      headers.join(','),
      ...sorted.map((r) => headers.map((c) => `"${cellStr(r[c]).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `workspace_${data.flowId}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearAllFilters = () => { setSearch(''); setColFilters({}); setSortCol(null); };
  const hasFilters = search || Object.values(colFilters).some(Boolean);

  const btnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 3,
    border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)',
    color: '#fff', fontSize: 11, fontWeight: 500, cursor: 'pointer', transition: 'all 0.1s',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999998,
      background: '#f0f0f0', display: 'flex', flexDirection: 'column',
      fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif",
    }}>
      {/* Ribbon */}
      <div style={{
        flexShrink: 0, display: 'flex', flexDirection: 'column',
        background: '#217346', color: '#fff',
      }}>
        <div style={{
          height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="2" width="20" height="20" rx="2" fill="#fff" fillOpacity="0.2" />
              <rect x="5" y="6" width="6" height="2" rx="0.5" fill="#fff" />
              <rect x="5" y="10" width="6" height="2" rx="0.5" fill="#fff" />
              <rect x="5" y="14" width="6" height="2" rx="0.5" fill="#fff" />
              <rect x="13" y="6" width="6" height="2" rx="0.5" fill="#fff" fillOpacity="0.5" />
              <rect x="13" y="10" width="6" height="2" rx="0.5" fill="#fff" fillOpacity="0.5" />
              <rect x="13" y="14" width="6" height="2" rx="0.5" fill="#fff" fillOpacity="0.5" />
            </svg>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Workspace</span>
            <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 3 }}>
              {data.flowId}
            </span>
          </div>
          <button onClick={onClose} style={btnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >× Kapat <span style={{ fontSize: 9, opacity: 0.5 }}>Esc</span></button>
        </div>
        {/* Toolbar */}
        <div style={{
          height: 36, display: 'flex', alignItems: 'center', gap: 6,
          padding: '0 16px', background: 'rgba(0,0,0,0.15)',
        }}>
          <div style={{ position: 'relative', flex: '0 0 240px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5"
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            ><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Tüm kolonlarda ara..."
              style={{
                width: '100%', padding: '4px 8px 4px 26px', borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)',
                color: '#fff', fontSize: 11, outline: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
              onBlur={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
            />
          </div>
          <button onClick={exportXLSX} style={btnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            XLSX
          </button>
          <button onClick={exportCSV} style={btnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            CSV
          </button>
          {hasFilters && (
            <button onClick={clearAllFilters} style={{ ...btnStyle, background: 'rgba(255,80,80,0.25)', borderColor: 'rgba(255,80,80,0.4)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,80,80,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,80,80,0.25)'; }}
            >
              × Filtreleri Temizle
            </button>
          )}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            {sorted.length === data.rows.length
              ? `${data.rows.length} satır`
              : `${sorted.length} / ${data.rows.length} satır`
            } · {data.columns.length} kolon
          </span>
        </div>
      </div>

      {/* Formula Bar */}
      <div style={{
        height: 26, flexShrink: 0, display: 'flex', alignItems: 'center',
        borderBottom: '1px solid #c0c0c0', background: '#fff', padding: '0 8px', fontSize: 12,
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 70, height: 18, borderRight: '1px solid #c0c0c0', marginRight: 8,
          fontWeight: 600, fontSize: 11, color: '#333', background: '#f8f9fa',
        }}>Ticket</span>
        <span style={{ color: '#555' }}>#{ticketId} — {data.flowId}</span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
        {sorted.length > 0 || data.rows.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "Calibri, Arial, sans-serif", fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th style={{
                  padding: '4px 8px', textAlign: 'center', fontSize: 11, fontWeight: 600,
                  color: '#333', background: '#e7e6e6', borderBottom: '2px solid #b4b4b4',
                  borderRight: '1px solid #c0c0c0', width: 42,
                }}>#</th>
                {data.columns.map((col) => {
                  const isActive = sortCol === col;
                  const hasFilter = colFilters[col];
                  return (
                    <th key={col} style={{
                      padding: 0, textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: '#333', background: hasFilter ? '#dce8dc' : '#e7e6e6',
                      borderBottom: '2px solid #b4b4b4', borderRight: '1px solid #c0c0c0',
                      whiteSpace: 'nowrap', userSelect: 'none', minWidth: 80, position: 'relative',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <button
                          onClick={() => handleSort(col)}
                          style={{
                            flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                            padding: '5px 6px 5px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                            color: '#333', display: 'flex', alignItems: 'center', gap: 3,
                          }}
                        >
                          {col}
                          {isActive && <span style={{ color: '#217346', fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowFilterFor(showFilterFor === col ? null : col); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
                            color: hasFilter ? '#217346' : '#999', fontSize: 10,
                          }}
                          title={`${col} filtrele`}
                        >▼</button>
                      </div>
                      {showFilterFor === col && (
                        <div ref={filterRef} style={{
                          position: 'absolute', top: '100%', left: 0, zIndex: 100,
                          background: '#fff', border: '1px solid #c0c0c0', borderRadius: 4,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 8, minWidth: 180,
                        }}>
                          <input
                            autoFocus type="text"
                            value={colFilters[col] || ''}
                            onChange={(e) => setColFilters((p) => ({ ...p, [col]: e.target.value }))}
                            placeholder={`${col} filtrele...`}
                            style={{
                              width: '100%', padding: '4px 8px', border: '1px solid #c0c0c0',
                              borderRadius: 3, fontSize: 11, outline: 'none', boxSizing: 'border-box',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#217346'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#c0c0c0'; }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setShowFilterFor(null); }}
                          />
                          {colFilters[col] && (
                            <button
                              onClick={() => { setColFilters((p) => { const n = { ...p }; delete n[col]; return n; }); setShowFilterFor(null); }}
                              style={{
                                marginTop: 6, width: '100%', padding: '3px 0', border: '1px solid #e0e0e0',
                                borderRadius: 3, background: '#f8f8f8', color: '#666', fontSize: 10,
                                cursor: 'pointer', textAlign: 'center',
                              }}
                            >Filtreyi Temizle</button>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e8f4f8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = rowIdx % 2 === 0 ? '#fff' : '#fafafa'; }}
                  style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}
                >
                  <td style={{
                    padding: '3px 8px', textAlign: 'center', fontSize: 10, color: '#888', fontWeight: 600,
                    borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #c0c0c0', background: '#e7e6e6',
                  }}>{rowIdx + 1}</td>
                  {data.columns.map((col) => {
                    const display = cellStr(row[col]);
                    return (
                      <td key={col} style={{
                        padding: '3px 10px', color: '#333', fontSize: 12,
                        borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e8e8e8',
                        maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={display}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={data.columns.length + 1} style={{
                    padding: 40, textAlign: 'center', color: '#999', fontSize: 13,
                  }}>
                    {hasFilters ? 'Filtrelere uygun sonuç bulunamadı' : 'Veri bulunamadı'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', gap: 10, color: '#888',
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c0c0c0" strokeWidth="1.2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Bu bölmede tablo verisi bulunamadı</span>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div style={{
        height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', borderTop: '1px solid #c0c0c0', background: '#217346',
        fontSize: 10, color: 'rgba(255,255,255,0.8)',
      }}>
        <span>Hazır</span>
        <div style={{ display: 'flex', gap: 16 }}>
          {hasFilters && <span>Filtrelendi: {sorted.length} / {data.rows.length}</span>}
          <span>{(hasFilters ? sorted : data.rows).length} satır × {data.columns.length} kolon</span>
        </div>
      </div>
    </div>
  );
};

function LeftPanelTickets() {
  const {
    tickets,
    selectTicket,
    eventCache,
    allFields,
    selectedTicket,
    selectedTicketId,
    setSelectedTicket,
    setSelectedTicketId,
    memberList,
    fetchTickets,
    loading: ticketsLoading,
  } = useContext(TicketContext);
  const {
    notificationCounters: wsNotificationCounters,
    setNotificationCounters: setWsNotificationCounters,
    ticketsToMoveToTop,
  } = useContext(TicketWebSocketContext);
  const { detailViewRequest, clearDetailViewRequest } = useContext(PanelContext);

  const [detailTicket, setDetailTicket] = useState(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [validImages, setValidImages] = useState({});
  const [searchFilter, setSearchFilter] = useState(() => () => true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMainflows, setSelectedMainflows] = useState([]);
  const [visitedTickets, setVisitedTickets] = useState(new Set());
  const ticketListRef = useRef(null);
  const [expandedMainflows, setExpandedMainflows] = useState(new Set());
  const [expandedFlows, setExpandedFlows] = useState(new Set());
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const previousSelectedTicketIdRef = useRef(null); // DetailView'ın otomatik açılmasını kontrol etmek için
  const lastHandledDetailRequestAtRef = useRef(0);

  const SESSION_STORAGE_KEY = 'jubi_visited_tickets';
  const LAST_OPENED_TICKET_KEY = 'jubi_last_opened_ticket';
  const [lastOpenedTicketId, setLastOpenedTicketId] = useState(null);

  // sessionStorage'dan ziyaret edilen ticket'ları yükle
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const visitedIds = JSON.parse(stored);
        setVisitedTickets(new Set(visitedIds));
      }
    } catch (error) {
      console.warn('Ziyaret edilen ticket\'lar yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
      if (stored) {
        setLastOpenedTicketId(stored);
      }
    } catch (error) {
      console.warn('Son açılan ticket yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
      if (stored) {
        setLastOpenedTicketId(stored);
      }
    } catch (error) {
      console.warn('Son açılan ticket yüklenemedi:', error);
    }
  }, []);

  // Ziyaret edilen ticket'ı işaretle
  const markTicketAsVisited = useCallback((ticketId) => {
    if (!ticketId) return;

    setVisitedTickets((prev) => {
      const newSet = new Set(prev);
      newSet.add(String(ticketId));
      
      // sessionStorage'a kaydet
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(Array.from(newSet)));
      } catch (error) {
        console.warn('Ziyaret edilen ticket kaydedilemedi:', error);
      }
      
      return newSet;
    });
  }, []);

  // Ticket'ın ziyaret edilip edilmediğini kontrol et
  const isTicketVisited = useCallback((ticketId) => {
    if (!ticketId) return false;
    return visitedTickets.has(String(ticketId));
  }, [visitedTickets]);

  const translations = {
    'tickets.myProcess': 'My Flows',
    'tickets.noDescription': 'Açıklama yok',
    'tickets.noTickets': 'Gösterilecek bilet yok',
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      // Status filtresini kontrol et
      const statusMatch =
        activeFilter === 'all' ||
        ticket.STATUS === activeFilter ||
        (typeof ticket.STATUS === 'object' && ticket.STATUS?.value === activeFilter);
      
      // Arama ve mainflow filtrelerini kontrol et
      const searchMatch = searchFilter(ticket);
      
      return statusMatch && searchMatch;
    });
  }, [tickets, searchFilter, activeFilter]);

  // Ticket'ları MAINFLOW ve FLOW'a göre kategorize et
  const categorizedTickets = useMemo(() => {
    const categories = {};
    
    filteredTickets.forEach((ticket) => {
      // MAINFLOW bilgisini al
      let mainflowKey = 'N/A';
      if (ticket?.MAINFLOW_TEXT) {
        mainflowKey = ticket.MAINFLOW_TEXT;
      } else if (ticket?.MAINFLOW) {
        if (typeof ticket.MAINFLOW === 'string') {
          mainflowKey = ticket.MAINFLOW;
        } else if (typeof ticket.MAINFLOW === 'object' && ticket.MAINFLOW?.value) {
          mainflowKey = ticket.MAINFLOW.value;
        }
      }
      
      // FLOW bilgisini al
      let flowKey = 'N/A';
      if (ticket?.FLOWID) {
        flowKey = ticket.FLOWID;
      } else if (ticket?.FLOW) {
        if (typeof ticket.FLOW === 'string') {
          flowKey = ticket.FLOW;
        } else if (typeof ticket.FLOW === 'object' && ticket.FLOW?.value) {
          flowKey = ticket.FLOW.value;
        }
      }
      
      // Kategori yapısını oluştur
      if (!categories[mainflowKey]) {
        categories[mainflowKey] = {};
      }
      
      if (!categories[mainflowKey][flowKey]) {
        categories[mainflowKey][flowKey] = [];
      }
      
      categories[mainflowKey][flowKey].push(ticket);
    });
    
    return categories;
  }, [filteredTickets]);

  // 🎫 TICKET ID BAZLI FİLTRELEME: allFields'ı seçili ticket için cache'den veya global state'den al
  const currentTicketAllFields = useMemo(() => {
    if (!selectedTicketId) {
      return null;
    }

    // Önce cache'den kontrol et
    const cacheEntry = eventCache[selectedTicketId];
    if (cacheEntry && cacheEntry.allFields) {
      return cacheEntry.allFields;
    }

    // Cache'de yoksa, global allFields'ı kullan (sadece seçili ticket için güncelleniyor)
    return allFields;
  }, [selectedTicketId, eventCache, allFields]);

  const getLastEventDate = (ticketId) => {
    const cacheEntry = eventCache[ticketId];
    if (!cacheEntry || !Array.isArray(cacheEntry.events) || cacheEntry.events.length === 0) {
      return null;
    }

    const sortedEvents = [...cacheEntry.events].sort((a, b) => {
      const aId = parseInt(a.EVEID) || 0;
      const bId = parseInt(b.EVEID) || 0;
      return bId - aId;
    });

    return sortedEvents[0]?.ANDAT || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih yok';

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'Geçersiz tarih';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const eventDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (eventDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      }

      if (eventDate.getTime() === yesterday.getTime()) {
        return 'dün';
      }

      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return 'Tarih hatası';
    }
  };

  const handleOpenLastTicket = useCallback(() => {
    const storedId = lastOpenedTicketId || sessionStorage.getItem(LAST_OPENED_TICKET_KEY);
    if (!storedId) return;
    const ticket = tickets.find(t => String(t.TICID) === String(storedId));
    if (ticket) {
      handleTicketSelection(ticket);
    }
  }, [lastOpenedTicketId, tickets]);

  const handleRefreshList = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchTickets();
    } catch (e) {
      console.error('Liste yenilenemedi:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchTickets, isRefreshing]);

  const handleSearch = useCallback((filterFn) => {
    if (typeof filterFn === 'function') {
      // Sadece arama ve mainflow filtrelerini uygula, status filtresi filteredTickets içinde uygulanıyor
      setSearchFilter(() => filterFn);
    } else {
      // Filtre yoksa tüm ticket'ları göster (status filtresi filteredTickets içinde uygulanıyor)
      setSearchFilter(() => () => true);
    }
  }, []);

  const handleSearchTermChange = (term) => {
    setSearchTerm(term);
  };

  const handleMainflowsChange = (mainflows) => {
    setSelectedMainflows(mainflows);
  };

  // activeFilter değişikliği filteredTickets useMemo'sunda işleniyor, burada ek işlem gerekmiyor

  useEffect(() => {
    if (ticketsToMoveToTop.size > 0 && ticketListRef.current) {
      const updatedTicketId = Array.from(ticketsToMoveToTop)[0];
      const updatedTicketElement = ticketListRef.current.querySelector(
        `[data-ticket-id="${updatedTicketId}"]`,
      );

      if (updatedTicketElement) {
        updatedTicketElement.classList.add('highlight-update');
        updatedTicketElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });

        setTimeout(() => {
          updatedTicketElement.classList.remove('highlight-update');
        }, 2000);
      }
    }
  }, [ticketsToMoveToTop]);

  useEffect(() => {
    const checks = {};
    tickets.forEach((ticket) => {
      if (ticket?.TICID) {
        checks[ticket.TICID] = false;
      }
    });
    setValidImages(checks);
  }, [tickets]);

  // Dışarıdan gelen "Detail View aç" isteklerini (NewTicket, RightToolbar vb.) güvenle işle
  useEffect(() => {
    if (!detailViewRequest?.ticketId || !detailViewRequest?.requestedAt) return;
    if (lastHandledDetailRequestAtRef.current === detailViewRequest.requestedAt) return;

    lastHandledDetailRequestAtRef.current = detailViewRequest.requestedAt;
    const reqTicketId = String(detailViewRequest.ticketId);
    const listTicket = tickets.find((t) => String(t?.TICID) === reqTicketId);

    if (listTicket) {
      setDetailTicket(listTicket);
      setIsDetailView(true);
    } else if (selectedTicket && String(selectedTicket.TICID) === reqTicketId) {
      setDetailTicket(selectedTicket);
      setIsDetailView(true);
    } else {
      // Geçici fallback: veri gelir gelmez selectedTicket ile senkronlanacak
      setDetailTicket({ TICID: reqTicketId });
      setIsDetailView(true);
    }

    if (String(selectedTicketId || '') !== reqTicketId) {
      setSelectedTicketId(reqTicketId);
      Promise.resolve(selectTicket(reqTicketId)).catch((error) => {
        console.error('DetailView ticket seçimi başarısız:', error);
      });
    }

    clearDetailViewRequest();
  }, [
    clearDetailViewRequest,
    detailViewRequest,
    selectTicket,
    selectedTicket,
    selectedTicketId,
    setSelectedTicketId,
    tickets,
  ]);

  // 🎫 TICKET ID BAZLI FİLTRELEME: DetailView sadece seçili ticket için gösterilmeli
  // NOT: handleReturnToList ile DetailView kapatıldığında bu useEffect tekrar açmamalı
  useEffect(() => {
    // Eğer selectedTicketId değişmediyse ve DetailView zaten kapalıysa, hiçbir şey yapma
    // (kullanıcı geri butonuna basmış olabilir)
    if (previousSelectedTicketIdRef.current === selectedTicketId && !isDetailView) {
      return;
    }
    
    // selectedTicketId değiştiyse ref'i güncelle
    const ticketIdChanged = previousSelectedTicketIdRef.current !== selectedTicketId;
    previousSelectedTicketIdRef.current = selectedTicketId;
    
    // selectedTicketId kontrolü
    if (selectedTicketId) {
      // Eğer selectedTicket varsa ve ID'ler eşleşiyorsa, detailTicket'ı güncelle
      // selectedTicket güncellendiğinde (selectTicket çağrısından sonra) detailTicket'ı da güncelle
      if (selectedTicket && String(selectedTicket.TICID) === String(selectedTicketId)) {
        // selectedTicket güncellendiğinde detailTicket'ı da güncelle
        // İlk tıklamada handleTicketSelection zaten detailTicket'ı set etti,
        // burada sadece selectedTicket güncellendiğinde detailTicket'ı güncelliyoruz
        if (
          ticketIdChanged ||
          !isDetailView ||
          !detailTicket ||
          String(detailTicket.TICID) !== String(selectedTicketId)
        ) {
          setDetailTicket(selectedTicket);
        }
        if (!isDetailView) setIsDetailView(true);
      }
      // Eğer selectedTicket henüz yoksa ama detailTicket zaten set edilmişse (handleTicketSelection'dan),
      // DetailView'ı açık tut - selectedTicket güncellenene kadar bekleyecek
      // Bu durumda hiçbir şey yapma, handleTicketSelection zaten DetailView'ı açmış
    } else {
      // selectedTicketId yoksa DetailView'ı kapat
      setDetailTicket(null);
      setIsDetailView(false);
    }

  }, [selectedTicket, selectedTicketId, isDetailView, detailTicket]);

  // Tarayıcı title'ını detailTicket ile güncelle
  useEffect(() => {
    if (detailTicket) {
      const mainflow = detailTicket.MAINFLOW || detailTicket.TDESC || '';
      const ticid = detailTicket.TICID || '';
      document.title = `${mainflow} — #${ticid}`;
    } else {
      document.title = 'DiagRun';
    }
    return () => {
      document.title = 'DiagRun';
    };
  }, [detailTicket]);

  // Ticket seçimi temizlendiğinde expanded state'leri temizleme
  // Kullanıcı dropdown'ların açık kalmasını istiyor, bu yüzden bu useEffect kaldırıldı
  // useEffect(() => {
  //   if (!selectedTicketId && !selectedTicket) {
  //     setExpandedMainflows(new Set());
  //     setExpandedFlows(new Set());
  //     setSelectedFlowId(null);
  //   }
  // }, [selectedTicketId, selectedTicket]);

  // Ticket değiştiğinde seçili flow'u sıfırla
  useEffect(() => {
    if (detailTicket) {
      setSelectedFlowId(null);
    }
  }, [detailTicket?.TICID]);

  const resetWsNotification = (ticketId, e) => {
    e.stopPropagation();
    setWsNotificationCounters((prev) => {
      const newCounters = { ...prev };
      delete newCounters[ticketId];
      return newCounters;
    });
  };

  const myProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'myProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'myProcess'),
      ).length,
    [tickets],
  );

  const onGoingProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'onGoingProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'onGoingProcess'),
      ).length,
    [tickets],
  );

  const completedProcess = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket?.STATUS === 'completedProcess' ||
          (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'completedProcess'),
      ).length,
    [tickets],
  );

  const handleTicketSelection = (ticket) => {
    if (!ticket?.TICID) {
      console.warn('⚠️ LeftPanelTickets - Ticket TICID yok:', ticket);
      return;
    }

    // Ticket'ı ziyaret edildi olarak işaretle
    markTicketAsVisited(ticket.TICID);
    setLastOpenedTicketId(String(ticket.TICID));
    try {
      sessionStorage.setItem(LAST_OPENED_TICKET_KEY, String(ticket.TICID));
    } catch (error) {
      console.warn('Son açılan ticket kaydedilemedi:', error);
    }
    setLastOpenedTicketId(String(ticket.TICID));
    try {
      sessionStorage.setItem(LAST_OPENED_TICKET_KEY, String(ticket.TICID));
    } catch (error) {
      console.warn('Son açılan ticket kaydedilemedi:', error);
    }

    // 🎫 TICKET ID BAZLI FİLTRELEME: Seçili ticket ID'sini güncelle
    // Eğer aynı ticket tekrar seçiliyorsa bile state'i güncelle ki useEffect tetiklensin
    const ticketId = ticket.TICID;
    setSelectedTicketId(ticketId);
    
    // İlk tıklamada DetailView'ı hemen aç ve detailTicket'ı set et
    // selectedTicket henüz güncellenmemiş olabilir, bu yüzden ticket objesini direkt kullan
    setDetailTicket(ticket);
    setIsDetailView(true);
    
    // selectTicket çağrıldığında selectedTicket ve allFields güncellenecek
    // DetailView useEffect'inde selectedTicket değiştiğinde detailTicket güncellenecek
    selectTicket(ticketId);

    if (wsNotificationCounters[ticketId] > 0) {
      setWsNotificationCounters((prev) => {
        const newCounters = { ...prev };
        delete newCounters[ticketId];
        return newCounters;
      });
    }
  };

  const handleReturnToList = () => {
    // Sadece DetailView'ı kapat, ticket seçimini koru
    // selectedTicketId ve selectedTicket korunmalı ki RightPanelToolbar ve diğer component'ler
    // seçili ticket bilgisini görebilsin
    setDetailTicket(null);
    setIsDetailView(false);
    // selectedTicketId ve selectedTicket'ı TEMİZLEME - korunmalı!
    // Expanded state'leri temizleme - kullanıcı dropdown'ların açık kalmasını istiyor
    // setExpandedMainflows(new Set());
    // setExpandedFlows(new Set());
    // setSelectedFlowId(null);
  };

  const deriveOwnerName = (ticket) => {
    if (!ticket) return 'N/A';
    if (ticket.UNAME) return ticket.UNAME;

    const listEntry = tickets.find((item) => String(item.TICID) === String(ticket.TICID));
    if (listEntry?.UNAME) return listEntry.UNAME;

    return 'N/A';
  };

  const renderTicketSummary = (ticket) => (
    <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/6 px-4 py-4 text-sm shadow-[0_15px_40px_rgba(8,18,38,0.25)]">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-blue-100/70">Ticket</p>
          <h3 className="text-lg font-semibold text-white">#{ticket.TICID}</h3>
        </div>
        <span className="rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold text-blue-200">
          {ticket.STATUS?.value || ticket.STATUS || 'N/A'}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Sahibi</p>
          <p className="text-sm font-medium text-slate-100">
            {deriveOwnerName(ticket)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Akış</p>
          <p className="text-sm font-medium text-slate-100">
            {ticket.MAINFLOW_TEXT || ticket.MAINFLOW || 'N/A'}
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Açıklama</p>
          <p className="text-xs leading-relaxed text-slate-200">
            {ticket.USEREXP || translations['tickets.noDescription']}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Son Güncelleme</p>
          <p className="text-sm font-medium text-slate-100">
            {formatDate(
              ticket.lastEventDate || ticket.LAST_EVENTDATE || ticket.ANDAT || getLastEventDate(ticket.TICID),
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <>
      <header className="flex-shrink-0 border-b border-[#22324b] bg-[#22324b] shadow-sm">
        <LeftPanelTopBar 
          onSearch={handleSearch}
          searchTerm={searchTerm}
          selectedMainflows={selectedMainflows}
          onSearchTermChange={handleSearchTermChange}
          onMainflowsChange={handleMainflowsChange}
        />
      </header>

      <div className="flex-shrink-0 border-b border-[#273754] bg-[#323F53] px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filtreleri */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'all', label: 'Tümü', count: tickets.length },
              { key: 'onGoingProcess', label: 'Devam Edenler', count: onGoingProcess },
              { key: 'myProcess', label: 'Bende Bekleyenler', count: myProcess },
              { key: 'completedProcess', label: 'Tamamlananlar', count: completedProcess },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors duration-150 ${
                  activeFilter === key
                    ? 'bg-blue-500 text-white'
                    : 'bg-[#2A3D5C] text-slate-200 hover:bg-[#314568]'
                }`}
                onClick={() => setActiveFilter(key)}
              >
                <span>{label}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    activeFilter === key 
                      ? 'bg-white/25 text-white' 
                      : 'bg-[#34476a] text-slate-100'
                  }`}
                >
                  {count}
                </span>
              </button>
            ))}
            <button
              onClick={handleRefreshList}
              disabled={isRefreshing}
              className="p-1.5 rounded-lg text-slate-300 transition-colors duration-150 hover:bg-[#314568] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Listeyi yenile"
            >
              <FaSyncAlt size={11} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleOpenLastTicket}
              disabled={!lastOpenedTicketId}
              className="p-1.5 rounded-lg text-slate-300 transition-colors duration-150 hover:bg-[#314568] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Son açılan ticket'a git"
            >
              <FaHistory size={11} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={ticketListRef}
        className="ticket-list flex-1 overflow-y-auto bg-[#1A283F]"
        style={{ scrollbarWidth: 'thin' }}
      >
        {ticketsLoading ? (
          <div className="flex flex-col gap-0.5 p-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-lg bg-[#22324b]/60 p-3 animate-pulse" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-center gap-2.5">
                  <div className="h-7 w-7 rounded-full bg-slate-600/40 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-2.5 w-3/5 rounded bg-slate-600/40" />
                    <div className="h-2 w-2/5 rounded bg-slate-700/30" />
                  </div>
                  <div className="h-4 w-10 rounded-full bg-slate-600/30" />
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(categorizedTickets).length > 0 ? (
          <div className="divide-y divide-[#22324b]/50">
            {Object.entries(categorizedTickets)
              .sort(([a], [b]) => a.localeCompare(b, 'tr'))
              .map(([mainflowKey, flows]) => {
                const mainflowId = `mainflow-${mainflowKey}`;
                const isMainflowExpanded = expandedMainflows.has(mainflowKey);
                const mainflowTicketCount = Object.values(flows).reduce((sum, tickets) => sum + tickets.length, 0);

                return (
                  <div key={mainflowKey} className="border-b border-[#22324b]/30">
                    {/* Ana Kategori: MAINFLOW */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMainflows((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(mainflowKey)) {
                            newSet.delete(mainflowKey);
                          } else {
                            newSet.add(mainflowKey);
                          }
                          return newSet;
                        });
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors duration-150 text-left border-l-4 ${
                        isMainflowExpanded
                          ? 'bg-blue-600/25 border-blue-400'
                          : 'bg-slate-700/50 border-slate-500 hover:bg-slate-600/60'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <svg
                          className={`h-5 w-5 transition-transform duration-200 ${
                            isMainflowExpanded ? 'rotate-90 text-blue-300' : 'text-slate-300'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className={`font-bold text-[12px] ${
                          isMainflowExpanded ? 'text-blue-100' : 'text-slate-100'
                        }`}>
                          {mainflowKey}
                        </span>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                          isMainflowExpanded
                            ? 'bg-blue-500/80 text-white shadow-md'
                            : 'bg-slate-600/70 text-slate-200'
                        }`}>
                          {mainflowTicketCount}
                        </span>
                      </div>
                    </button>

                    {/* Alt Kategoriler: FLOW */}
                    {isMainflowExpanded && (
                      <div className="bg-gradient-to-b from-slate-800/40 to-slate-900/30">
                        {Object.entries(flows)
                          .sort(([a], [b]) => a.localeCompare(b, 'tr'))
                          .map(([flowKey, tickets]) => {
                            const flowId = `flow-${mainflowKey}-${flowKey}`;
                            const isFlowExpanded = expandedFlows.has(flowId);

                            return (
                              <div key={flowKey} className="border-l-4 border-emerald-500/60 ml-4 my-1">
                                {/* Alt Kategori Header */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedFlows((prev) => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(flowId)) {
                                        newSet.delete(flowId);
                                      } else {
                                        newSet.add(flowId);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  className={`w-full flex items-center justify-between px-5 py-2.5 transition-colors duration-150 text-left ${
                                    isFlowExpanded
                                      ? 'bg-emerald-600/30'
                                      : 'bg-slate-600/40 hover:bg-slate-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 flex-1">
                                    <svg
                                      className={`h-4 w-4 transition-transform duration-200 ${
                                        isFlowExpanded ? 'rotate-90 text-emerald-300' : 'text-slate-300'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span className={`font-semibold text-[11px] ${
                                      isFlowExpanded ? 'text-emerald-100' : 'text-slate-200'
                                    }`}>
                                      {flowKey}
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                      isFlowExpanded
                                        ? 'bg-emerald-500/80 text-white shadow-sm'
                                        : 'bg-slate-500/60 text-slate-200'
                                    }`}>
                                      {tickets.length}
                                    </span>
                                  </div>
                                </button>

                                {/* Ticket Listesi */}
                                {isFlowExpanded && (
                                  <ul className="divide-y divide-emerald-400/20 ml-2">
                                    {[...tickets]
                                      .sort((a, b) => {
                                        // Ticketları oluşturulma/oluşma tarihine göre sırala (en yeni üstte)
                                        const getCreatedTs = (t) => {
                                          const raw =
                                            t?.ANDAT ??
                                            t?.created_at ??
                                            t?.CREATED_AT ??
                                            t?.createdAt ??
                                            t?.LAST_EVENTDATE ??
                                            t?.lastEventDate ??
                                            null;
                                          if (!raw) return 0;
                                          const d = new Date(raw);
                                          const ts = d.getTime();
                                          return Number.isNaN(ts) ? 0 : ts;
                                        };
                                        const aTs = getCreatedTs(a);
                                        const bTs = getCreatedTs(b);
                                        if (aTs !== bTs) return bTs - aTs;

                                        // Tie-breaker: TICID büyük olan üstte
                                        const aId = parseInt(a?.TICID, 10) || 0;
                                        const bId = parseInt(b?.TICID, 10) || 0;
                                        return bId - aId;
                                      })
                                      .map((ticket) => (
                                      <li
                                        key={ticket?.TICID || Math.random()}
                                        data-ticket-id={ticket?.TICID}
                                        onClick={() => handleTicketSelection(ticket)}
                                        className={`group relative flex cursor-pointer items-start gap-3 px-5 py-2.5 text-[10.5px] transition-colors duration-150 border-l-4 ${
                                          selectedTicketId === ticket?.TICID
                                            ? 'bg-blue-50 border-blue-500'
                                            : 'bg-white/90 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                        } ${ticketsToMoveToTop.has(ticket?.TICID) ? 'updated' : ''}`}
                                      >
                                        <div className="flex items-center justify-center rounded-full p-1 transition-colors bg-[#d5def0] text-[#2b4a7a]">
                                          <FaUserCircle className="h-4 w-4" />
                                        </div>

                                          <div className="flex min-w-0 flex-1 items-start gap-3">
                                          <div className="min-w-0 flex-1 space-y-1.5">
                                            <p className="truncate text-[11px] font-semibold text-slate-800">
                                              {(() => {
                                                if (ticket?.UNAME && typeof ticket.UNAME === 'object') {
                                                  try {
                                                    return JSON.stringify(ticket.UNAME);
                                                  } catch (e) {
                                                    return 'N/A';
                                                  }
                                                }
                                                if (ticket?.UNAME && typeof ticket.UNAME === 'string') {
                                                  return ticket.UNAME;
                                                }
                                                return 'N/A';
                                              })()}
                                            </p>
                                            <p className="truncate text-[10.5px] font-medium text-slate-700">
                                              {(() => {
                                                if (ticket?.USEREXP && typeof ticket.USEREXP === 'object') {
                                                  try {
                                                    return JSON.stringify(ticket.USEREXP);
                                                  } catch (e) {
                                                    return translations['tickets.noDescription'];
                                                  }
                                                }
                                                if (ticket?.USEREXP && typeof ticket.USEREXP === 'string') {
                                                  return ticket.USEREXP;
                                                }
                                                return translations['tickets.noDescription'];
                                              })()}
                                            </p>
                                          </div>

                                          <div className="flex flex-col items-end gap-1 text-[9.5px] text-slate-500">
                                            {isTicketVisited(ticket?.TICID) && (
                                              <div
                                                className="flex items-center justify-center rounded-full bg-blue-500/10 p-1 transition-colors group-hover:bg-blue-500/20"
                                                title="Bu ticket daha önce görüntülendi"
                                              >
                                                <HiEye className="h-3 w-3 text-blue-600" />
                                              </div>
                                            )}
                                            <div className="text-right text-[9.5px] text-slate-500">
                                              {(() => {
                                                const createdAt =
                                                  ticket?.ANDAT ||
                                                  ticket?.created_at ||
                                                  ticket?.updated_at ||
                                                  ticket?.LAST_EVENTDATE ||
                                                  getLastEventDate(ticket?.TICID);
                                                if (!createdAt) {
                                                  return <div className="text-[9.5px] text-slate-400">Tarih yok</div>;
                                                }
                                                const dateObj = new Date(createdAt);
                                                if (Number.isNaN(dateObj.getTime())) {
                                                  return <div className="text-[9.5px] text-slate-400">Geçersiz tarih</div>;
                                                }
                                                return (
                                                  <>
                                                    <div className="font-medium text-slate-600">
                                                      {dateObj.toLocaleDateString('tr-TR')}
                                                    </div>
                                                    <div className="text-slate-500">
                                                      {dateObj.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="ml-auto flex items-center gap-2">
                                          <button
                                            className={`rounded-full p-1.5 transition-colors ${
                                              ticket?.STATUS === 'await' ||
                                              (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'await')
                                                ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
                                                : ticket?.STATUS === 'continue' ||
                                                  (typeof ticket?.STATUS === 'object' && ticket?.STATUS?.value === 'continue')
                                                ? 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                                                : 'bg-slate-500/10 text-slate-600 hover:bg-slate-500/20'
                                            }`}
                                            title="Bileti düzenle"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setTicketToEdit(ticket);
                                              setIsModalOpen(true);
                                            }}
                                          >
                                            <FiSettings className="h-3.5 w-3.5" />
                                          </button>
                                          {wsNotificationCounters[ticket?.TICID] > 0 && (
                                            <button
                                              className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[9.5px] font-semibold text-white transition-colors hover:bg-orange-600"
                                              onClick={(e) => resetWsNotification(ticket?.TICID, e)}
                                              title="WebSocket bildirimini sıfırla"
                                            >
                                              {wsNotificationCounters[ticket?.TICID]}
                                            </button>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-[11px] text-slate-500">
            {translations['tickets.noTickets']}
          </div>
        )}
      </div>
    </>
  );

  // Flow görselleştirmesi için event'lerden flow'ları çıkar
  const getTicketFlows = useMemo(() => {
    if (!detailTicket?.TICID) return [];

    const cacheEntry = eventCache[detailTicket.TICID];
    if (!cacheEntry || !Array.isArray(cacheEntry.events) || cacheEntry.events.length === 0) {
      return [];
    }

    // Event'leri tarih sırasına göre sırala
    const sortedEvents = [...cacheEntry.events].sort((a, b) => {
      const aDate = new Date(a.ANDAT || 0);
      const bDate = new Date(b.ANDAT || 0);
      return aDate - bDate;
    });

    // Benzersiz flow'ları çıkar (ilk görüldüğü sıraya göre)
    const flowMap = new Map();
    sortedEvents.forEach((event) => {
      const flowId = event.FLOWID || event.FLOW || null;
      if (flowId && !flowMap.has(flowId)) {
        flowMap.set(flowId, {
          flowId,
          firstSeenDate: event.ANDAT,
          eventCount: 1,
        });
      } else if (flowId) {
        flowMap.get(flowId).eventCount += 1;
      }
    });

    return Array.from(flowMap.values());
  }, [detailTicket, eventCache]);

  // Mevcut flow'u belirle
  const getCurrentFlow = useMemo(() => {
    if (!detailTicket) return null;
    return detailTicket.FLOWID || detailTicket.FLOW || null;
  }, [detailTicket]);

  // İlk flow'u otomatik seç (detailTicket veya flow'lar değiştiğinde)
  useEffect(() => {
    if (getTicketFlows.length > 0) {
      // Eğer seçili flow yoksa veya mevcut flow'lar arasında yoksa ilk flow'u seç
      if (!selectedFlowId || !getTicketFlows.find(flow => flow.flowId === selectedFlowId)) {
        setSelectedFlowId(getTicketFlows[0].flowId);
      }
    } else {
      // Flow yoksa seçimi temizle
      setSelectedFlowId(null);
    }
  }, [detailTicket?.TICID, getTicketFlows]);

  // Seçili flow'un event'lerini al
  const getSelectedFlowEvents = useMemo(() => {
    if (!selectedFlowId || !detailTicket?.TICID) return null;
    
    const cacheEntry = eventCache[detailTicket.TICID];
    if (!cacheEntry || !Array.isArray(cacheEntry.events)) return null;

    const flowEvents = cacheEntry.events.filter(event => 
      (event.FLOWID || event.FLOW) === selectedFlowId
    );


    return flowEvents;
  }, [selectedFlowId, detailTicket, eventCache]);

  // Seçili flow'un allFields'ını al - allFields içinde flow ID'si section key olarak kullanılıyor
  const getSelectedFlowAllFields = useMemo(() => {
    if (!selectedFlowId || !detailTicket?.TICID) return null;
    
    // Cache'deki allFields'ı al
    const cacheEntry = eventCache[detailTicket.TICID];
    const baseAllFields = cacheEntry?.allFields || currentTicketAllFields;
    
    if (!baseAllFields || typeof baseAllFields !== 'object') return null;

    // allFields içinde seçili flow'un section'ını bul
    // Flow ID'si section key olarak kullanılıyor (talepOlustur, teklifTopla, vb.)
    if (baseAllFields[selectedFlowId]) {
      return {
        [selectedFlowId]: baseAllFields[selectedFlowId]
      };
    }

    // Eğer section yoksa null döndür (event listesi gösterilecek)
    return null;
  }, [selectedFlowId, detailTicket, eventCache, currentTicketAllFields]);

  // Seçili flow'un renk şemasını al
  const getSelectedFlowColorScheme = useMemo(() => {
    if (!selectedFlowId) return null;
    
    const flows = getTicketFlows;
    const flowIndex = flows.findIndex(flow => flow.flowId === selectedFlowId);
    
    if (flowIndex === -1) return null;

    const flowColors = [
      { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-400/50', text: 'text-blue-200', dot: 'bg-blue-500' },
      { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-400/50', text: 'text-purple-200', dot: 'bg-purple-500' },
      { bg: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-400/50', text: 'text-emerald-200', dot: 'bg-emerald-500' },
      { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-400/50', text: 'text-amber-200', dot: 'bg-amber-500' },
      { bg: 'from-rose-500/20 to-rose-600/10', border: 'border-rose-400/50', text: 'text-rose-200', dot: 'bg-rose-500' },
    ];

    return flowColors[flowIndex % flowColors.length];
  }, [selectedFlowId, getTicketFlows]);

  // Flow görselleştirme component'i (eski büyük versiyon)
  const renderFlowVisualization = () => {
    const flows = getTicketFlows;
    const currentFlow = getCurrentFlow;
    if (flows.length === 0) {
      return (
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-800/50 to-slate-900/50 px-6 py-8 shadow-lg">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="rounded-full bg-slate-700/50 p-4">
              <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-400">Flow bilgisi bulunamadı</p>
            <p className="text-xs text-slate-500">Ticket henüz herhangi bir flow'dan geçmemiş</p>
          </div>
        </div>
      );
    }

    const cacheEntry = detailTicket?.TICID ? eventCache[detailTicket.TICID] : null;
    const baseAllFields = cacheEntry?.allFields || currentTicketAllFields;
    const allEvents = Array.isArray(cacheEntry?.events) ? cacheEntry.events : [];

    return (
      <div className="p-2 space-y-2">
        {flows.map((flow, index) => {
          const isCurrentFlow = flow.flowId === currentFlow;
          const flowEvents = allEvents.filter(
            (event) => (event.FLOWID || event.FLOW) === flow.flowId,
          );
          const flowAllFields =
            baseAllFields && typeof baseAllFields === 'object' && baseAllFields[flow.flowId]
              ? { [flow.flowId]: baseAllFields[flow.flowId] }
              : null;

          return (
            <div key={`${flow.flowId}-${index}`} className="rounded-md overflow-hidden bg-[#1a2436] border border-slate-700/50">
              {/* Section Header */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-[#1e2b3f]">
                <span className="text-[10px] font-bold text-slate-400 tabular-nums w-4 text-center">{index + 1}</span>
                <div className="w-px h-3.5 bg-slate-600/50" />
                <h4 className="text-[12px] font-semibold text-slate-100 truncate">
                  {flow.flowId}
                </h4>
                {isCurrentFlow && (
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold text-emerald-400 bg-emerald-500/10">
                    aktif
                  </span>
                )}
                <div className="flex-1" />
                <span className="text-[9px] text-slate-400 tabular-nums">{flow.firstSeenDate ? formatDate(flow.firstSeenDate) : ''}</span>
                <span className="text-[9px] text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded tabular-nums">{flow.eventCount} event</span>
                {flowAllFields && Object.keys(flowAllFields).length > 0 && (
                  <button
                    onClick={() => openWorkspace(flow.flowId, flowAllFields, flowEvents)}
                    className="text-slate-400 hover:text-slate-200 transition-colors p-0.5"
                    title="Workspace"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Section Body */}
              {flowAllFields && Object.keys(flowAllFields).length > 0 ? (
                <div className="px-3 py-2">
                  <FormView formData={flowAllFields} flowEvents={flowEvents} />
                </div>
              ) : (
                <div className="px-3 py-3 text-center">
                  <span className="text-[10px] text-slate-500">
                    {flowEvents && flowEvents.length > 0 ? 'Form detayları bulunamadı' : 'Detay bilgisi bulunamadı'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Açılır/kapanır dropdown component helper
  const CollapsibleSection = ({ title, isOpen, onToggle, children, icon, noPadding = false, maxHeight = '70vh' }) => {
    return (
      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-800/50 to-slate-900/50 overflow-hidden shadow-lg flex flex-col">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors duration-150 group flex-shrink-0"
          type="button"
        >
          <div className="flex items-center gap-3">
            {icon && (
              <div className="text-slate-400 group-hover:text-slate-300 transition-colors">
                {icon}
              </div>
            )}
            <h3 className="text-sm font-semibold text-slate-200 group-hover:text-white transition-colors">
              {title}
            </h3>
          </div>
          <svg
            className={`h-5 w-5 text-slate-400 group-hover:text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          className={`overflow-hidden ${
            isOpen ? 'opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease' }}
        >
          <div 
            className={`overflow-y-auto collapsible-content ${noPadding ? '' : 'p-4 border-t border-white/10'}`} 
            style={{ 
              scrollbarWidth: 'thin',
              maxHeight: isOpen ? maxHeight : '0',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!workspaceData) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setWorkspaceData(null); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [workspaceData]);

  const openWorkspace = useCallback((flowId, flowAllFields, flowEvents) => {
    const rows = [];
    if (flowAllFields) {
      Object.values(flowAllFields).forEach((section) => {
        const entries = Array.isArray(section) ? section : [section];
        entries.forEach((entry) => {
          if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            rows.push(entry);
          }
        });
      });
    }
    if (rows.length === 0 && flowEvents?.length > 0) {
      flowEvents.forEach((ev) => {
        const row = {};
        if (ev.FNAME) row.FNAME = ev.FNAME;
        if (ev.FVALUE) row.FVALUE = ev.FVALUE;
        if (ev.TDESC) row.TDESC = ev.TDESC;
        if (ev.ANDAT) row.ANDAT = ev.ANDAT;
        if (Object.keys(row).length > 0) rows.push(row);
      });
    }
    const allKeys = [...new Set(rows.flatMap((r) => Object.keys(r)))];
    setWorkspaceData({ flowId, rows, columns: allKeys });
  }, []);

  const renderDetailView = () => {
    return (
      <div className="flex flex-1 flex-col overflow-hidden bg-[#141d2e] text-slate-100">
        {/* Header */}
        <div className="flex-shrink-0 h-12 border-b border-white/10 bg-white/5 px-3">
          <div className="flex h-12 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3 className="text-sm font-semibold text-white whitespace-nowrap">
                #{detailTicket?.TICID || 'N/A'}
              </h3>
              {detailTicket && (
                <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-200 border border-blue-400/15 whitespace-nowrap">
                  {detailTicket.STATUS?.value || detailTicket.STATUS || 'N/A'}
                </span>
              )}
              {isTicketVisited(detailTicket?.TICID) && (
                <HiEye className="h-3.5 w-3.5 text-blue-300/80 flex-shrink-0" title="Görüntülendi" />
              )}
              {detailTicket && (
                <span className="hidden sm:inline text-[11px] text-slate-300/80 truncate min-w-0">
                  {detailTicket.MAINFLOW_TEXT || detailTicket.MAINFLOW || ''}
                </span>
              )}
            </div>
            <button
              onClick={handleReturnToList}
              className="rounded-lg border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-slate-100 transition hover:border-white/30 hover:bg-white/10 whitespace-nowrap flex-shrink-0"
            >
              ← Geri
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto detail-view-scroll" style={{ scrollbarWidth: 'thin' }}>
            {detailTicket && getTicketFlows.length > 0 ? (
              renderFlowVisualization()
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="rounded-full bg-slate-700/50 p-4">
                  <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-400">Flow bilgisi bulunamadı</p>
                <p className="text-xs text-slate-500">Ticket henüz herhangi bir flow'dan geçmemiş</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="flex h-full flex-col bg-[#1A283F] text-slate-200">
      {isDetailView && detailTicket ? renderDetailView() : renderListView()}

      {isModalOpen && (
        <EditTicketModal ticket={ticketToEdit} onClose={() => setIsModalOpen(false)} />
      )}

      {workspaceData && createPortal(
        <WorkspaceOverlay data={workspaceData} ticketId={detailTicket?.TICID} onClose={() => setWorkspaceData(null)} />,
        document.body
      )}
    </section>
  );
}

export default LeftPanelTickets;
